import { apiClient, ApiError } from './apiClient';
import { API_BASE_URL } from '../constants/api';
import type {
  BusinessProfileResponse,
  BusinessProfileDetail,
  BusinessProfileFormData,
  CreateBusinessProfileRequest
} from '../types/businessProfile';

export const businessProfileService = {
  /**
   * Fetches the business profile dashboard data including facilities and time slots
   * Requires authentication token
   */
  async getDashboard(token: string): Promise<BusinessProfileResponse> {
    return apiClient.get<BusinessProfileResponse>(
      '/business-profile/dashboard',
      token
    );
  },

  /**
   * Get current user's business profile with full details
   */
  async getProfile(token: string): Promise<BusinessProfileDetail> {
    return apiClient.get<BusinessProfileDetail>(
      '/business-profile',
      token
    );
  },

  /**
   * Create new business profile
   */
  async createProfile(
    token: string,
    data: BusinessProfileFormData,
    facilityPlan?: File
  ): Promise<BusinessProfileDetail> {
    if (facilityPlan) {
      const formData = new FormData();

      // Append all form fields
      const requestData = mapFormDataToRequest(data);
      Object.entries(requestData).forEach(([key, value]) => {
        if (value !== null && value !== undefined) {
          formData.append(key, String(value));
        }
      });
      formData.append('facilityPlan', facilityPlan);

      // Append schedule data as JSON strings
      appendScheduleToFormData(formData, data);

      return this.postFormData<BusinessProfileDetail>(
        '/business-profile/multipart',
        formData,
        token
      );
    }

    return apiClient.post<BusinessProfileDetail>(
      '/business-profile',
      mapFormDataToRequestWithSchedule(data),
      token
    );
  },

  /**
   * Update existing business profile
   */
  async updateProfile(
    token: string,
    profileId: string,
    data: BusinessProfileFormData,
    facilityPlan?: File
  ): Promise<BusinessProfileDetail> {
    if (facilityPlan) {
      const formData = new FormData();

      // Append all form fields
      const requestData = mapFormDataToRequest(data);
      Object.entries(requestData).forEach(([key, value]) => {
        if (value !== null && value !== undefined) {
          formData.append(key, String(value));
        }
      });
      formData.append('facilityPlan', facilityPlan);

      // Append schedule data as JSON strings
      appendScheduleToFormData(formData, data);

      return this.putFormData<BusinessProfileDetail>(
        `/business-profile/${profileId}/multipart`,
        formData,
        token
      );
    }

    return apiClient.put<BusinessProfileDetail>(
      `/business-profile/${profileId}`,
      mapFormDataToRequestWithSchedule(data),
      token
    );
  },

  /**
   * Upload business avatar
   */
  async uploadAvatar(
    token: string,
    profileId: string,
    file: File
  ): Promise<{ avatarUrl: string }> {
    const formData = new FormData();
    formData.append('avatar', file);

    return this.postFormData<{ avatarUrl: string }>(
      `/business-profile/${profileId}/avatar`,
      formData,
      token
    );
  },

  /**
   * POST request with FormData
   */
  async postFormData<T>(
    endpoint: string,
    formData: FormData,
    token: string
  ): Promise<T> {
    const url = `${API_BASE_URL}${endpoint}`;

    const response = await fetch(url, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${token}`,
        'accept': 'text/plain',
      },
      body: formData,
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => null);
      throw new ApiError(
        errorData?.message || `HTTP ${response.status}: ${response.statusText}`,
        response.status,
        errorData
      );
    }

    return response.json();
  },

  /**
   * PUT request with FormData
   */
  async putFormData<T>(
    endpoint: string,
    formData: FormData,
    token: string
  ): Promise<T> {
    const url = `${API_BASE_URL}${endpoint}`;

    const response = await fetch(url, {
      method: 'PUT',
      headers: {
        'Authorization': `Bearer ${token}`,
        'accept': 'text/plain',
      },
      body: formData,
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => null);
      throw new ApiError(
        errorData?.message || `HTTP ${response.status}: ${response.statusText}`,
        response.status,
        errorData
      );
    }

    return response.json();
  },
};

function mapFormDataToRequest(data: BusinessProfileFormData): CreateBusinessProfileRequest {
  return {
    nip: data.nip,
    companyName: data.companyName,
    displayName: data.displayName,
    address: data.address,
    city: data.city,
    postalCode: data.postalCode,
    email: data.email,
    phoneNumber: data.phoneNumber,
    phoneCountry: data.phoneCountry || 'PL',
    regon: data.regon || undefined,
    krs: data.krs || undefined,
    legalForm: data.legalForm,
    categoryId: data.categoryId,
    website: data.website,
    websiteDescription: data.websiteDescription,
    contactPersonName: data.contactPersonName,
    contactPersonSurname: data.contactPersonSurname,
    latitude: data.latitude ?? undefined,
    longitude: data.longitude ?? undefined,
    termsAndConditionsUrl: data.termsAndConditionsUrl || undefined,
  };
}

// Convert schedule slots to API format (PascalCase for C# DTO)
function convertScheduleSlots(slots: Array<{ id?: string; time: string; isAvailable: boolean }> | undefined) {
  if (!slots || slots.length === 0) return [];
  return slots.map(slot => ({
    Id: slot.id || slot.time,
    Time: slot.time,
    IsAvailable: slot.isAvailable,
  }));
}

// Convert date-specific availability to API format (PascalCase for C# DTO)
function convertDateSpecificAvailability(dateSpecific: Record<string, Array<{ time: string; isAvailable: boolean }>> | undefined) {
  if (!dateSpecific) return {};
  const result: Record<string, Array<{ Time: string; IsAvailable: boolean }>> = {};
  for (const [date, slots] of Object.entries(dateSpecific)) {
    result[date] = slots.map(slot => ({
      Time: slot.time,
      IsAvailable: slot.isAvailable,
    }));
  }
  return result;
}

// Append schedule data to FormData as JSON strings
function appendScheduleToFormData(formData: FormData, data: BusinessProfileFormData) {
  formData.append('WeekdaysSchedule', JSON.stringify(convertScheduleSlots(data.weekdaysSchedule)));
  formData.append('SaturdaySchedule', JSON.stringify(convertScheduleSlots(data.saturdaySchedule)));
  formData.append('SundaySchedule', JSON.stringify(convertScheduleSlots(data.sundaySchedule)));
  formData.append('DateSpecificAvailability', JSON.stringify(convertDateSpecificAvailability(data.dateSpecificAvailability)));
}

// Map form data to request including schedule (for JSON requests)
function mapFormDataToRequestWithSchedule(data: BusinessProfileFormData) {
  return {
    ...mapFormDataToRequest(data),
    weekdaysSchedule: convertScheduleSlots(data.weekdaysSchedule),
    saturdaySchedule: convertScheduleSlots(data.saturdaySchedule),
    sundaySchedule: convertScheduleSlots(data.sundaySchedule),
    dateSpecificAvailability: convertDateSpecificAvailability(data.dateSpecificAvailability),
  };
}
